import { useEffect, useState } from "react";
import { Bell, BellOff, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

// A soft browser-notification nudge that fires at the learner's chosen time,
// but only on days their streak is at risk (i.e., they haven't been active
// yet that day). We schedule locally via setInterval so we don't need any
// push-server infra; the app just has to be open in a tab (or installed as
// PWA and opened once that day) for the reminder to fire.

const SUPPORTS_NOTIFICATIONS = typeof window !== "undefined" && "Notification" in window;
const LAST_FIRED_KEY = "lur_reminder_last_fired";

export default function PracticeReminderCard() {
    const { user, refresh } = useAuth();
    const [enabled, setEnabled] = useState(!!user?.reminder_enabled);
    const [time, setTime] = useState(user?.reminder_time || "18:30");
    const [saving, setSaving] = useState(false);
    const [permission, setPermission] = useState(
        SUPPORTS_NOTIFICATIONS ? Notification.permission : "denied"
    );

    // Keep local state in sync when the user object refreshes.
    useEffect(() => {
        setEnabled(!!user?.reminder_enabled);
        if (user?.reminder_time) setTime(user.reminder_time);
    }, [user?.reminder_enabled, user?.reminder_time]);

    const save = async (nextEnabled, nextTime) => {
        setSaving(true);
        try {
            await api.put("/settings/reminder", {
                enabled: nextEnabled,
                time: nextTime,
            });
            if (nextEnabled) toast.success("Reminder saved", { description: `We'll nudge you at ${nextTime} on days your streak needs it.` });
            else toast.success("Reminder turned off");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Could not save your reminder");
        } finally {
            setSaving(false);
        }
    };

    const requestPermissionAndEnable = async () => {
        if (!SUPPORTS_NOTIFICATIONS) {
            toast.error("This browser doesn't support notifications.");
            return;
        }
        let perm = Notification.permission;
        if (perm === "default") perm = await Notification.requestPermission();
        setPermission(perm);
        if (perm !== "granted") {
            toast.error("Notifications are blocked. Turn them on in your browser settings, then try again.");
            return;
        }
        setEnabled(true);
        await save(true, time);
    };

    const disable = async () => {
        setEnabled(false);
        await save(false, time);
    };

    const onTimeChange = async (e) => {
        const v = e.target.value; // "HH:MM"
        setTime(v);
        if (enabled) await save(true, v);
    };

    const permissionLabel =
        !SUPPORTS_NOTIFICATIONS
            ? "This browser can't show reminders."
            : permission === "granted"
                ? "Notifications allowed."
                : permission === "denied"
                    ? "Notifications are currently blocked in your browser settings."
                    : "We'll ask for permission when you turn this on.";

    return (
        <section className="card-rw p-7 mb-6" data-testid="reminder-card">
            <div className="flex items-start gap-5 mb-4">
                <div className="w-12 h-12 rounded-lg bg-[#FDE9DD] flex items-center justify-center flex-shrink-0">
                    {enabled ? (
                        <Bell className="w-6 h-6 text-[#D47A55]" strokeWidth={1.6} />
                    ) : (
                        <BellOff className="w-6 h-6 text-[#D47A55]" strokeWidth={1.6} />
                    )}
                </div>
                <div className="flex-1 min-w-[220px]">
                    <h2 className="text-xl font-medium text-[#1C2321] mb-1" style={{ fontFamily: "Outfit" }}>
                        Daily practice reminder
                    </h2>
                    <p className="text-[#515B57]">
                        A soft nudge at your chosen time — only on the days your streak is at risk. No pings on days you've already practiced.
                    </p>
                </div>
            </div>

            <div className="grid sm:grid-cols-[auto_auto_1fr] items-center gap-4 pt-2">
                <label className="text-sm text-[#1C2321]">
                    <span className="label-eyebrow block mb-1">Nudge time</span>
                    <input
                        type="time"
                        value={time}
                        onChange={onTimeChange}
                        disabled={saving}
                        className="px-3 py-2 rounded-lg border border-[#E5E7E2] focus:outline-none focus:border-[#2D5D4E] bg-white text-base"
                        data-testid="reminder-time-input"
                    />
                </label>
                <div className="flex items-center gap-2">
                    {enabled ? (
                        <button
                            onClick={disable}
                            disabled={saving}
                            data-testid="reminder-disable"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] font-medium hover:bg-[#F5F1E4] disabled:opacity-60"
                        >
                            <BellOff className="w-4 h-4" strokeWidth={1.8} /> Turn off
                        </button>
                    ) : (
                        <button
                            onClick={requestPermissionAndEnable}
                            disabled={saving}
                            data-testid="reminder-enable"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#D47A55] text-white font-medium hover:bg-[#B86644] disabled:opacity-60"
                        >
                            <Bell className="w-4 h-4" strokeWidth={1.8} /> Turn on
                        </button>
                    )}
                    {enabled && permission === "granted" && (
                        <span className="inline-flex items-center gap-1 text-sm text-[#2D5D4E]" data-testid="reminder-status-on">
                            <Check className="w-4 h-4" strokeWidth={2} /> On
                        </span>
                    )}
                </div>
                <p className="text-xs text-[#819888]" data-testid="reminder-permission-label">
                    {permissionLabel}
                </p>
            </div>
        </section>
    );
}

// -------------------------------------------------------------------------
// Client-side scheduler. Mounted once at the app root (see App.jsx) so it
// keeps a single 60s tick regardless of route. Fires a browser Notification
// when: user is signed in + reminder_enabled + Notification.permission granted
// + current wall-clock minute === reminder_time + not yet fired today + user
// hasn't been active today (last_active_date != today).
// -------------------------------------------------------------------------
export function PracticeReminderScheduler() {
    const { user } = useAuth();

    useEffect(() => {
        if (!SUPPORTS_NOTIFICATIONS) return;
        if (!user?.reminder_enabled || !user?.reminder_time) return;
        if (Notification.permission !== "granted") return;

        const tick = () => {
            try {
                const now = new Date();
                const hh = String(now.getHours()).padStart(2, "0");
                const mm = String(now.getMinutes()).padStart(2, "0");
                const nowHM = `${hh}:${mm}`;
                const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

                if (user.last_active_date === todayIso) return; // already practiced
                if (localStorage.getItem(LAST_FIRED_KEY) === todayIso) return; // already nudged

                // Fuzzy match: fire if we're AT or PAST the target time. This way,
                // a backgrounded/throttled tab still fires the reminder when it
                // wakes up after the target minute — better than silently missing.
                if (nowHM < user.reminder_time) return;

                // Claim the day BEFORE firing so a second tab racing the same
                // minute reads the key back and bails. Not bulletproof across
                // tabs (localStorage is not atomic) but combined with the
                // shared Notification `tag`, duplicates are collapsed.
                localStorage.setItem(LAST_FIRED_KEY, todayIso);

                const n = new Notification("Lift Up Reading", {
                    body: "A short session keeps your streak alive. Five minutes is enough.",
                    icon: "/icons/icon-192.png",
                    badge: "/icons/icon-192.png",
                    tag: "lur-daily-reminder",
                });
                n.onclick = () => {
                    window.focus();
                    window.location.href = "/app/dashboard";
                };
            } catch {
                /* noop */
            }
        };

        // Fire once immediately in case the user opened the app right on the minute,
        // then every 60 seconds thereafter.
        tick();
        const id = setInterval(tick, 60_000);
        return () => clearInterval(id);
    }, [user?.reminder_enabled, user?.reminder_time, user?.last_active_date]);

    return null;
}
